// src/components/issues/CarteFiche.tsx — SCE Issues
// Briques des cartes de la colonne de droite de la fiche problème
// (Équipement, Suivi, Documentation) : une seule définition, pour que la
// section Documentation (Lot 13, SectionDocumentation.tsx) ait exactement
// l'aspect des cartes voisines.

import type React from 'react'
import { ChevronRight } from 'lucide-react'

export const STYLE_CARTE: React.CSSProperties = {
  backgroundColor: 'var(--color-white)',
  border: '1px solid var(--color-border)',
  borderRadius: 12,
  padding: '4px 16px',
}

export const STYLE_TITRE_CARTE: React.CSSProperties = {
  margin: '12px 0 4px',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--color-gray-70)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
}

export const STYLE_LIGNE_SUIVI: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '104px minmax(0, 1fr)',
  gap: 12,
  padding: '10px 0',
  borderTop: '1px solid var(--color-gray-10)',
  fontSize: 14,
}

/**
 * Une ligne de la carte Suivi. `discrete` : valeur absente ou en attente.
 * Avec `onOuvrir`, toute la ligne ouvre une fiche, une flèche l'annonce —
 * même convention que la carte Équipement.
 */
export function LigneSuivi({
  label,
  children,
  discrete,
  action,
  onOuvrir,
  libelleOuvrir,
}: {
  label: string
  children: React.ReactNode
  discrete?: boolean
  action?: React.ReactNode
  onOuvrir?: () => void
  libelleOuvrir?: string
}): JSX.Element {
  if (onOuvrir) {
    return (
      <button
        type="button"
        onClick={onOuvrir}
        aria-label={libelleOuvrir}
        className="w-full text-left items-center"
        style={{ ...STYLE_LIGNE_SUIVI, border: 'none', borderTop: STYLE_LIGNE_SUIVI.borderTop, background: 'transparent', color: 'inherit', minHeight: 44 }}
      >
        <span style={{ color: 'var(--color-gray-70)' }}>{label}</span>
        <span className="flex items-center justify-between gap-2" style={{ minWidth: 0 }}>
          <span style={{ fontWeight: 500, color: 'var(--color-text-primary)', minWidth: 0, overflowWrap: 'anywhere' }}>{children}</span>
          <ChevronRight size={18} color="var(--color-gray-60)" style={{ flexShrink: 0 }} />
        </span>
      </button>
    )
  }
  return (
    <div style={STYLE_LIGNE_SUIVI}>
      <span style={{ color: 'var(--color-gray-70)' }}>{label}</span>
      <div className="flex items-start justify-between gap-2" style={{ minWidth: 0 }}>
        <span
          style={{
            minWidth: 0,
            // Une référence sans espace (« NVS50312-G-EDE Rev 01 », un N° de
            // plan) se coupe plutôt que de sortir de la carte.
            overflowWrap: 'anywhere',
            whiteSpace: 'pre-line',
            fontWeight: discrete ? 400 : 500,
            fontStyle: discrete ? 'italic' : 'normal',
            color: discrete ? 'var(--color-gray-70)' : 'var(--color-text-primary)',
          }}
        >
          {children}
        </span>
        {action}
      </div>
    </div>
  )
}

/**
 * Une ligne d'action de carte (piste B du 06/10) : icône, titre, ce que ça
 * veut dire en une ligne, flèche. « Ouvrir une FNC-T », « Déclarer une NC-S ».
 */
export function LigneActionCarte({
  icone,
  titre,
  detail,
  onClick,
  infobulle,
}: {
  icone: React.ReactNode
  titre: React.ReactNode
  detail?: React.ReactNode
  onClick: () => void
  infobulle?: string
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      title={infobulle}
      className="flex items-center gap-3 w-full text-left"
      style={{
        minHeight: 52,
        padding: '6px 0',
        border: 'none',
        borderTop: '1px solid var(--color-gray-10)',
        background: 'transparent',
        color: 'var(--color-black)',
        cursor: 'pointer',
      }}
    >
      <span style={{ flexShrink: 0, display: 'inline-flex' }}>{icone}</span>
      <span className="flex flex-col flex-1" style={{ minWidth: 0 }}>
        <span style={{ fontSize: 14, fontWeight: 600, overflowWrap: 'anywhere' }}>{titre}</span>
        {detail && <span style={{ fontSize: 12, color: 'var(--color-gray-70)' }}>{detail}</span>}
      </span>
      <ChevronRight size={18} color="var(--color-gray-60)" style={{ flexShrink: 0 }} />
    </button>
  )
}
